import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormGenerator, ModalWrapper } from '@microservices/wiskey-react-components'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Grid,
} from '@mui/material'

import { PageContext } from '@pages/FormCreateOrEdit'
import { useAddFieldDialog } from '@pages/FormCreateOrEdit/components/AddFieldDialog/hooks'

import { DropDownInputWithFitlers } from '@components/DropDownInputWithFitlers'
import { FilterDialog } from '@components/FilterDialog'
import { InputWithListVariables } from '@components/InputWithListVariables'
import { JavaScriptField } from '@components/JavaScriptField'
import { ModalFormContainer } from '@components/ModalFormContainer'
import { ScriptValueDialog } from '@components/ScriptValueDialog'

import { isObjectValueType } from '@helpers'
import { BIND_TYPE, DROPDOWN_FILTER_TYPES, MODAL_TYPE, PREFILL_OPTIONS, REGEX } from '@constants'
import { AutocompleteOption } from '@types'

import { ModalRefPathPicker } from '@gantt/components/GanttCreateOrEdit/components/ModalRefPathPicker'

import { FieldValueFormInputs } from './components/FieldValueFormInputs'
import { getDisabledWhenScriptConfig } from './helpers/getDisabledWhenScriptConfig'

export const AddFieldDialog: FC = () => {
  const { t } = useTranslation()

  const { data, state, handlers, inputs } = useAddFieldDialog()

  const { methods, valueType, code } = data

  const {
    fieldInputs,
    additionalInputs,
    dropwDownWindowFormInput,
    dropwDownListFormInput,
    preFillTypeInput,
    preFillStaticValueInput,
    objectCodeInput,
  } = inputs

  const {
    handleSubmit,
    formState: { isDirty, errors },
    watch,
    setValue,
  } = methods

  const {
    isShowFilterDialog,
    currentFilter,
    isLoading,
    isFieldsLoading,
    watchIsAddFieldValue,
    watchBinding,
  } = state

  const {
    handleSave,
    handleCloseFilterDialog,
    handleDeleteFilter,
    handleSubmitFilter,
    handleAddFilter,
    handleDoubleClickChip,
  } = handlers

  const {
    onCloseModal,
    showDialog,
    modalType,
    isShowScriptValueDialog,
    objectFieldsForFilter,
    toggleOpenScriptValueDialog,
    modifyObjectFields,
    currentForm,
    isFieldLoading,
    mainTable,
  } = useContext(PageContext)

  const watchValue = watch('value')
  const watchedDropDownListFilters = watch('dropDownListFilters')
  const watchDropdownList = watch('dropDownListCode')
  const watchedDropDownWindowFilters = watch('dropDownWindowFilters')
  const watchDropdownWindow = watch('dropDownWindowCode')
  const watchedPathArray = watch('pathArray')
  const watchedTargetPathArray = watch('targetPathArray')
  const watchedObjectValue = watch('objectValue')
  const watchPreFillSourceObject = watch('preFillSourceObject')
  const watchPreFillFilterObject = watch('preFillFilterObject')
  const watchPreFillType = watch('preFillType')
  const watchDropDownObject = watch('dropDownObjectCode')
  const watchEmbeddedObject = watch('embeddedObjectCode')
  const watchBindType = watch('bindType')
  const watchValueType = watch('valueType')

  const isShowDisplayedFieldWarning = useMemo(() => {
    return (
      watchValue &&
      watchBindType === BIND_TYPE.FIELD &&
      watchedPathArray.length &&
      isObjectValueType(watchValueType) &&
      watchDropDownObject?.id !== watchEmbeddedObject?.id &&
      !watchedPathArray.some(item => item.objectCode === watchEmbeddedObject?.id)
    )
  }, [
    watchValue,
    watchBindType,
    watchedPathArray,
    watchValueType,
    watchDropDownObject?.id,
    watchEmbeddedObject?.id,
  ])

  const isShowTargetPathWarning = useMemo(
    () =>
      Boolean(
        watchValue &&
          watchBindType === BIND_TYPE.FIELD &&
          watchedTargetPathArray?.length &&
          isObjectValueType(watchValueType) &&
          watchDropDownObject?.id !== watchEmbeddedObject?.id &&
          !watchedTargetPathArray?.some(item => item.objectCode === watchEmbeddedObject?.id)
      ),
    [
      watchValue,
      watchBindType,
      watchedTargetPathArray,
      watchValueType,
      watchDropDownObject?.id,
      watchEmbeddedObject?.id,
    ]
  )

  const [initialValue, setInitialValue] = useState({
    pathArray: watchedPathArray,
    objectValue: watchedObjectValue,
  })
  const [isShowDisabledWhenDialog, setIsShowDisabledWhenDialog] = useState(false)
  const watchConditionsDisabled = watch('conditionsDisabled')

  useEffect(() => {
    setInitialValue({
      pathArray: watchedPathArray,
      objectValue: watchedObjectValue,
    })
  }, [watchedObjectValue, watchedPathArray])

  const objectCode = useMemo(
    () => modifyObjectFields?.find(({ name }) => name === watchValue)?.model,
    [modifyObjectFields, watchValue]
  )

  const handleChangeSourceObject = (value: AutocompleteOption<string>) => {
    setValue('preFillSourceObject', value)
  }

  const handleChangeFilterObject = (value: AutocompleteOption<string>) => {
    setValue('preFillFilterObject', value)
  }

  const handleCloseModalWrapper = () => {
    onCloseModal(isDirty)
  }

  const handleToggleOpenScriptValueDialog = () => {
    toggleOpenScriptValueDialog(false)
  }

  const handleSaveScriptValueDialog = (value: string) => {
    setValue('value', value, { shouldDirty: true })
  }

  return (
    <ModalWrapper
      disabledSave={!isDirty || isFieldLoading}
      isShow={showDialog}
      title={`${t(`addDialog.${modalType}`)} Field`}
      containerStyle={{
        px: 0.5,
        py: 0.5,
        // borderRadius: 0,
        outline: 'none',
      }}
      onClose={handleCloseModalWrapper}
      onSave={handleSubmit(handleSave)}
    >
      {isShowFilterDialog && (
        <FilterDialog
          filter={currentFilter}
          isShow={isShowFilterDialog}
          onClose={handleCloseFilterDialog}
          onSubmit={handleSubmitFilter}
        />
      )}
      {isLoading && modalType === MODAL_TYPE.EDIT ? (
        <Grid container alignItems={'center'} justifyContent={'center'} my={3}>
          <CircularProgress />
        </Grid>
      ) : (
        <ModalFormContainer maxHeight={600}>
          <FormProvider {...methods}>
            {isShowScriptValueDialog && (
              <ScriptValueDialog
                windowBoundaries
                isJsx={watchBinding === BIND_TYPE.JSX}
                isShow={isShowScriptValueDialog}
                modalType={modalType}
                objectFields={objectFieldsForFilter}
                value={watchValue}
                onClose={handleToggleOpenScriptValueDialog}
                onSave={handleSaveScriptValueDialog}
              />
            )}
            <form>
              <FormGenerator inputs={fieldInputs} loading={isFieldsLoading} mainGridSpacing={0} />
              {watchBinding === BIND_TYPE.FIELD && Boolean(objectCode) && (
                <>
                  <ModalRefPathPicker
                    hasClearInInput
                    isRequired
                    controllerNamePathArray={`pathArray`}
                    controllerNamePathStr={`objectValue`}
                    currentValue={initialValue}
                    embeddedObjectPickerControllerName={`pathArray`}
                    label={t('label.displayField')}
                    name={`objectValue`}
                    object={watchDropDownObject}
                    placeHolder={t('placeholder.displayField')}
                    warningHint={
                      isShowDisplayedFieldWarning
                        ? t('form.addField.displayFieldMissingEmbeddedObject', {
                            fieldName: watchValue,
                          })
                        : undefined
                    }
                  />
                  <ModalRefPathPicker
                    hasClearInInput
                    controllerNamePathArray={`targetPathArray`}
                    controllerNamePathStr={`targetPathArrayValue`}
                    embeddedObjectPickerControllerName={`targetPathArray`}
                    label={t('label.targetPathArray')}
                    name={`targetPathArrayValue`}
                    object={watchDropDownObject}
                    placeHolder={t('placeholder.targetPathArray')}
                    warningHint={
                      isShowTargetPathWarning
                        ? t('form.addField.targetPathArrayMissingEmbeddedObject', {
                            fieldName: watchValue,
                          })
                        : undefined
                    }
                  />
                </>
              )}

              {isObjectValueType(valueType) && (
                <FormGenerator
                  inputs={objectCodeInput}
                  loading={isFieldsLoading}
                  mainGridSpacing={0}
                />
              )}

              <FormGenerator
                inputs={additionalInputs}
                loading={isFieldsLoading}
                mainGridSpacing={0}
              />
              {watchIsAddFieldValue && (
                <Box mb={1} ml={'120px'}>
                  <Accordion
                    sx={{
                      '&.MuiPaper-root': {
                        // borderRadius: 0,
                        border: errors['fieldValues'] ? '1px solid #d32f2f' : '1px solid #00000042',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      {t('fieldForm.fieldValues.title')}
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pr: 1 }}>
                      <FieldValueFormInputs
                        type={valueType}
                        valueReplacePattern={
                          code === 'internal_id' ? REGEX.MODEL_CODE_REPLACE_PATTERN : undefined
                        }
                      />
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}
              <FormGenerator
                inputs={preFillTypeInput}
                loading={isFieldsLoading}
                mainGridSpacing={0}
              />

              {watchPreFillType?.option === PREFILL_OPTIONS.DYNAMIC && (
                <ModalRefPathPicker
                  hasClearInInput
                  isRequired
                  embeddedObjectPickerControllerName='preFillLink'
                  isDisabled={false}
                  label={t('label.preFillSourceObject')}
                  name={`preFillLinkStr`}
                  object={watchPreFillSourceObject}
                  pickerName='preFillLink'
                  onChangeRootObject={handleChangeSourceObject}
                />
              )}

              {watchPreFillType?.option === PREFILL_OPTIONS.STATIC && (
                <>
                  <ModalRefPathPicker
                    hasClearInInput
                    isRequired
                    embeddedObjectPickerControllerName='preFillLink'
                    isDisabled={false}
                    label={t('label.preFillSourceObject')}
                    name={`preFillLinkStr`}
                    object={watchPreFillSourceObject}
                    pickerName='preFillLink'
                    onChangeRootObject={handleChangeSourceObject}
                  />
                  <ModalRefPathPicker
                    hasClearInInput
                    isRequired
                    embeddedObjectPickerControllerName='preFillFilterName'
                    isDisabled={false}
                    label={t('label.preFillFilterField')}
                    name={`preFillFilterNameStr`}
                    object={watchPreFillFilterObject}
                    pickerName='preFillFilterName'
                    onChangeRootObject={handleChangeFilterObject}
                  />
                  <FormGenerator
                    inputs={preFillStaticValueInput}
                    loading={isFieldsLoading}
                    mainGridSpacing={0}
                  />
                </>
              )}

              {isObjectValueType(valueType) && (
                <>
                  <InputWithListVariables
                    formId={currentForm?.id}
                    label={t('label.generalFilter')}
                    name={'generalFilterField'}
                  />
                  <DropDownInputWithFitlers
                    filters={watchedDropDownListFilters}
                    inputs={[dropwDownListFormInput]}
                    loading={isFieldsLoading}
                    watchInput={watchDropdownList}
                    onAddFilter={() => handleAddFilter(DROPDOWN_FILTER_TYPES.LIST)}
                    onDeleteFilter={filter =>
                      handleDeleteFilter(filter, DROPDOWN_FILTER_TYPES.LIST)
                    }
                    onDoubleClickFilter={filter =>
                      handleDoubleClickChip(filter, DROPDOWN_FILTER_TYPES.LIST)
                    }
                  />
                  <DropDownInputWithFitlers
                    filters={watchedDropDownWindowFilters}
                    inputs={[dropwDownWindowFormInput]}
                    loading={isFieldsLoading}
                    watchInput={watchDropdownWindow}
                    onAddFilter={() => handleAddFilter(DROPDOWN_FILTER_TYPES.WINDOW)}
                    onDeleteFilter={filter =>
                      handleDeleteFilter(filter, DROPDOWN_FILTER_TYPES.WINDOW)
                    }
                    onDoubleClickFilter={filter =>
                      handleDoubleClickChip(filter, DROPDOWN_FILTER_TYPES.WINDOW)
                    }
                  />
                </>
              )}
              <Box component='fieldset' mb={2} position='relative'>
                <legend>{t('contextMenuCreate.addMenuPointModal.conditionsDisabled.label')}</legend>
                <JavaScriptField
                  isShowScriptDialog={isShowDisabledWhenDialog}
                  language='js'
                  scriptValue={watchConditionsDisabled ?? ''}
                  onCloseScriptDialog={() => setIsShowDisabledWhenDialog(false)}
                  onOpenScriptDialog={() => setIsShowDisabledWhenDialog(true)}
                  onSaveScript={value =>
                    setValue('conditionsDisabled', value, { shouldDirty: true })
                  }
                  {...getDisabledWhenScriptConfig(objectFieldsForFilter, mainTable?.rows)}
                />
              </Box>
            </form>
          </FormProvider>
        </ModalFormContainer>
      )}
    </ModalWrapper>
  )
}
